import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { classifyPendingMainCategories } from "@/lib/mainCategory.functions";

export const Route = createFileRoute("/_adminLayout/admin/parts/main-categories")({
  head: () => ({ meta: [{ title: "RV / Auto / Marine Badges — Admin" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

function Page() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [log, setLog] = useState("");
  const { data: stats = [] } = useQuery({
    queryKey: ["main-cat-stats"],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("main_category_stats");
      if (error) throw error;
      return data as { source: string; total: number; rv: number; auto: number; marine: number }[];
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["main-cat-stats"] });

  const runRules = async () => {
    setBusy("rules");
    let total = 0;
    try {
      for (let i = 0; i < 60; i++) {
        const { data, error } = await (supabase.rpc as any)("apply_main_category_rules", { _limit: 3000 });
        if (error) throw error;
        const n = Number(data ?? 0);
        total += n;
        setLog(`Rules: ${total.toLocaleString()} items processed…`);
        if (n === 0) break;
      }
      setLog(`Rules finished: ${total.toLocaleString()} items processed.`);
    } catch (e: any) { setLog(`Rules stopped: ${e.message}`); }
    setBusy(null); refresh();
  };

  const runAi = async (batches: number) => {
    setBusy("ai");
    let done = 0;
    try {
      for (let i = 0; i < batches; i++) {
        const r = await classifyPendingMainCategories({ data: { limit: 40 } });
        done += r.classified;
        setLog(`AI: ${done} classified, ${r.remaining.toLocaleString()} still waiting`);
        if (!r.remaining) break;
      }
    } catch (e: any) { setLog(`AI stopped: ${e.message}`); }
    setBusy(null); refresh();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">RV / Auto / Marine Badges</h1>
        <p className="text-sm text-muted-foreground">Smart Hybrid: free rules first, then AI for items the rules can't decide. Cross-over parts can carry several badges.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={runRules} disabled={!!busy}>{busy === "rules" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Step 1 · Run free rules</Button>
        <Button variant="secondary" onClick={() => runAi(1)} disabled={!!busy}>Step 2 · AI test (40 items)</Button>
        <Button variant="secondary" onClick={() => runAi(25)} disabled={!!busy}>{busy === "ai" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}AI batch (up to 1,000)</Button>
      </div>
      {log && <p className="text-sm">{log}</p>}
      <Card>
        <CardHeader><CardTitle className="text-base">Status</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground"><th>Source</th><th>Items</th><th>RV</th><th>Auto</th><th>Marine</th></tr></thead>
            <tbody>{stats.map((s) => (
              <tr key={s.source} className="border-t"><td className="py-1">{s.source}</td><td>{Number(s.total).toLocaleString()}</td><td>{Number(s.rv).toLocaleString()}</td><td>{Number(s.auto).toLocaleString()}</td><td>{Number(s.marine).toLocaleString()}</td></tr>
            ))}</tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">rule = decided free · pending = waiting for AI · ai = decided by AI · review = AI unsure</p>
        </CardContent>
      </Card>
    </div>
  );
}
